import React from "react";
import { Link } from "react-router-dom";
import { Accessibility, Mail, Phone, ArrowRight } from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo from "@/components/Seo";

export default function AccessibilityPage() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Accessibility Statement"
        description="Magee Enterprises is committed to digital accessibility and equal access. Contact our accessibility team for accommodations."
        path="/accessibility"
        keywords="accessibility, ADA, WCAG, digital accessibility, accommodations, equal access"
      />

      {/* HERO */}
      <section className="relative pt-36 md:pt-44 pb-14 md:pb-20 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-45 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -right-32 opacity-35" />
        <HexMark size={220} opacity={0.055} className="absolute -left-10 top-24 rotate-[8deg] pointer-events-none hidden md:block" />
        <span className="corner-cross" style={{ top: 40, right: 40 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                § Legal · Accessibility
              </span>
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-700 border border-cyan-500/30 rounded-full px-2.5 py-1 bg-cyan-500/5 inline-flex items-center gap-1.5">
                <Accessibility size={11} /> WCAG 2.2 · Ongoing
              </span>
            </div>
            <MaskedReveal
              lines={["Access for", "everyone."]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[13vw] md:text-[8vw] lg:text-[7vw]"
              stagger={0.09}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                Magee Enterprises is committed to providing a website and services that are accessible to the widest possible audience — regardless of ability, technology, or context.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="mailto:dion@mageeenterprises.com?subject=Accessibility%20accommodation%20request"
                  className="btn-primary"
                  data-testid="accessibility-hero-email"
                >
                  Request an accommodation <ArrowRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern-dense opacity-30 pointer-events-none" />
        <div className="relative max-w-[1000px] mx-auto px-6 md:px-10 text-[15px] leading-[1.75] text-[var(--text)]/85 space-y-8">
          <div>
            <h2 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em] text-[var(--text)]">
              Our commitment
            </h2>
            <p className="mt-4">
              We believe accessibility is a right, not a feature. We continuously improve the accessibility of this website and the delivery of our services to meet or exceed the Web Content Accessibility Guidelines (<span className="text-[var(--text)] font-medium">WCAG 2.2, Level AA</span>) and the Americans with Disabilities Act (ADA).
            </p>
          </div>

          <div>
            <h2 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em] text-[var(--text)]">
              What we&rsquo;ve implemented
            </h2>
            <ul className="mt-4 space-y-2.5">
              <li>Semantic HTML landmarks and headings for screen-reader navigation.</li>
              <li>Descriptive alt text on informative images and logos.</li>
              <li>Keyboard-navigable menus, forms, and interactive elements.</li>
              <li>Sufficient color contrast for body copy and interactive states.</li>
              <li>Motion respects the <span className="text-[var(--text)] font-medium">prefers-reduced-motion</span> setting.</li>
              <li>Video content offered with captions or transcripts on request.</li>
              <li>Form fields with visible labels and inline error messaging.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em] text-[var(--text)]">
              Requesting an accommodation
            </h2>
            <p className="mt-4">
              If you have difficulty accessing any part of this website, or need materials in an alternative format (large print, plain text, audio, or braille), or require any accommodation to participate in an event we manage on your behalf, please reach out. We will respond within{" "}
              <span className="text-[var(--text)] font-medium">two (2) business days</span>{" "}
              and work with you to provide an equivalent experience.
            </p>

            <div className="mt-6 rounded-2xl border border-[var(--border)] bg-white p-6 md:p-8">
              <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-3">
                Accessibility contact
              </div>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                <a
                  href="mailto:dion@mageeenterprises.com?subject=Accessibility%20accommodation%20request"
                  className="inline-flex items-center gap-2 text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                  data-testid="accessibility-email"
                >
                  <Mail size={16} className="text-cyan-600" />
                  dion@mageeenterprises.com
                </a>
                <a
                  href="tel:+18882992250"
                  className="inline-flex items-center gap-2 text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                  data-testid="accessibility-phone"
                >
                  <Phone size={16} className="text-cyan-600" />
                  888-299-2250
                </a>
              </div>
              <p className="mt-4 text-[13.5px] text-[var(--text-muted)]">
                Please describe the barrier you encountered, the URL where it occurred, and the accommodation you need. If you rely on assistive technology, let us know which one so we can reproduce and resolve the issue.
              </p>
            </div>
          </div>

          <div>
            <h2 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em] text-[var(--text)]">
              Ongoing work
            </h2>
            <p className="mt-4">
              Accessibility is a moving target — we audit our site quarterly, retest with real assistive-technology users where possible, and prioritize fixes for any barriers reported to us. If our website ever falls short for you, please tell us. Your feedback is a gift.
            </p>
          </div>

          <div className="pt-4 border-t border-[var(--border)]">
            <p className="text-[13.5px] text-[var(--text-muted)]">
              Related: <Link to="/privacy" className="text-[var(--accent)] underline underline-offset-4 hover:no-underline">Privacy Policy</Link>
              {" · "}
              <Link to="/terms" className="text-[var(--accent)] underline underline-offset-4 hover:no-underline">Terms &amp; Conditions</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
